import { Injectable } from '@angular/core';
import {
  HttpInterceptor,
  HttpRequest,
  HttpHandler,
  HttpEvent,
  HttpErrorResponse,
} from '@angular/common/http';
import { catchError, switchMap, filter, take, finalize } from 'rxjs/operators';
import { Observable, throwError, BehaviorSubject } from 'rxjs';
import { Router } from '@angular/router';
import { environment } from 'src/environments/environment';
import { LoadingService } from '../../loading/loading.service';
import { UserService } from '../../user/user.service';
import { MatDialog } from '@angular/material/dialog';
import { DialogComponent } from 'src/app/components/dialog/dialog.component';
import { DialogModel } from 'src/app/components/dialog/models/DialogModel';
import { AuthModalService } from '../../auth/auth-modal.service';

@Injectable()
export class TokenInterceptor implements HttpInterceptor {
  private isRefreshing = false;
  private refreshTokenSubject: BehaviorSubject<any> = new BehaviorSubject<any>(
    null
  );
  private requestStartTime: number = 0;

  constructor(
    private auth: UserService,
    private loadingService: LoadingService,
    private router: Router,
    private dialog: MatDialog,
    private authModalService: AuthModalService
  ) {}

  intercept(
    request: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {
    this.requestStartTime = Date.now();

    let authReq = request;
    const token = this.auth.getAcessToken();

    const isApiRequest = request.url.includes(environment.apiUrl);
    const isAuthRequest = request.url.includes('auth/');
    const isArticlesRequest = request.url.includes('/articles');
    const isArticleRequest = request.url.includes('articleDetails/');
    const isChunkUpload =
      request.url.includes('training-interview/append-chunk') || request.url.includes('training-interview/append-chunk') || request.url.includes('api/training-interview/append-chunk');


    if (token && isApiRequest) {
      authReq = this.addToken(request, token);
    } else if (
      isApiRequest &&
      !isAuthRequest &&
      !isArticlesRequest &&
      !isArticleRequest
    ) {
      this.authModalService.open(this.router.url);
      return throwError(
        () =>
          new HttpErrorResponse({
            status: 401,
            error: { message: 'Authentication required' },
          })
      );
    }

    let checkInterval: any;
    if (isApiRequest && !isChunkUpload) {
      checkInterval = setInterval(() => {
        if (Date.now() - this.requestStartTime > 1000) {
          this.loadingService.showLoading();
          clearInterval(checkInterval);
        }
      }, 100);
    }

    return next.handle(authReq).pipe(
      catchError((error: HttpErrorResponse) => {
        if (checkInterval) {
          clearInterval(checkInterval);
        }

        if (error.status === 401 && isApiRequest && !isAuthRequest) {
          return this.handle401Error(authReq, next);
        } else if (
          error instanceof HttpErrorResponse &&
          isApiRequest &&
          error.status >= 500 &&
          !isChunkUpload
        ) {
          this.showErrorDialog(error);

          setTimeout(() => {
            requestAnimationFrame(() => {
              const content = document.querySelector('#app');
              if (content) content.scrollTop = 0;
            });
          }, 500);
        }

        return throwError(() => error);
      }),
      finalize(() => {
        if (checkInterval) {
          clearInterval(checkInterval);
        }
        if (isApiRequest && !isChunkUpload) {
          this.loadingService.hideLoading();
        }
      })
    );
  }

  private handle401Error(
    request: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {
    if (!this.isRefreshing) {
      this.isRefreshing = true;
      this.refreshTokenSubject.next(null);

      const refreshToken = this.auth.getRefreshToken();
      if (!refreshToken) {
        this.logoutAndNavigateToLogin();
        return throwError(
          () =>
            new HttpErrorResponse({
              status: 401,
              error: { message: 'No refresh token' },
            })
        );
      }

      return this.auth.refreshToken().pipe(
        switchMap((tokenResponse: any) => {
          this.isRefreshing = false;

          if (tokenResponse && tokenResponse.accessToken) {
            const newToken = tokenResponse.accessToken;
            this.auth.setAccessToken(newToken);
            this.refreshTokenSubject.next(newToken);
            return next.handle(this.addToken(request, newToken));
          } else {
            this.logoutAndNavigateToLogin();
            return throwError(
              () =>
                new HttpErrorResponse({
                  status: 401,
                  error: { message: 'Invalid token response' },
                })
            );
          }
        }),
        catchError((error: any) => {
          this.isRefreshing = false;
          this.refreshTokenSubject.next(null);
          this.logoutAndNavigateToLogin();
          return throwError(() => error);
        })
      );
    } else {
      return this.refreshTokenSubject.pipe(
        filter((token) => token !== null),
        take(1),
        switchMap((jwt) => {
          return next.handle(this.addToken(request, jwt));
        })
      );
    }
  }

  private addToken(request: HttpRequest<any>, token: string): HttpRequest<any> {
    const headersConfig: { [name: string]: string } = {
      Authorization: `Bearer ${token}`,
    };

    if (
      !request.headers.has('Content-Type') &&
      !(request.body instanceof FormData)
    ) {
      headersConfig['Content-Type'] = 'application/json';
    }

    return request.clone({
      setHeaders: headersConfig,
    });
  }

private logoutAndNavigateToLogin(): void {
  if (this.auth.isUserLogin()) {
    this.auth.logoutFromGoogleAccount();
  }
  const currentUrl = this.router.url;
  if (currentUrl && currentUrl !== '/login' && currentUrl !== '/sign-up') {
    this.authModalService.open(currentUrl);
  } else {
    this.router.navigate(['/login']);
  }
}
  private showErrorDialog(error: HttpErrorResponse): void {
    let title = 'Error';
    let content = 'Something went wrong, Please try again later.';

    if (error.error) {
      if (error.error.error) {
        title = error.error.code_error
          ? `Error ${error.error.code_error}`
          : 'Error';
        content = error.error.error;
      } else if (error.error.message) {
        content = error.error.message;
      } else if (error.error.text) {
        content = error.error.text;
      } else if (error.error.error_message) {
        content = error.error.error_message;
      }
    }

    if (error.status && error.status !== 200) {
      title = title.includes('Error') ? title : `Error ${error.status}`;
    }

    this.openDialog('400px', {
      status: 'error',
      title: title,
      content: content,
      okBtnText: 'Close',
    });
  }

  private openDialog(width: string = '400px', dialog: DialogModel) {
    return this.dialog.open(DialogComponent, {
      width: width,
      data: dialog,
    });
  }
}
